import { lazy, Suspense, useRef } from 'react'
import { SEOHead } from '@/components/seo/SEOHead'
import { buildOrganizationJsonLd, buildSoftwareApplicationJsonLd } from '@/lib/seo/jsonld'
import { PRICING_OFFERS } from '@/lib/marketing/pricing'
import { HomeDoors } from './HomeDoors'
import { ProofStrip } from './ProofStrip'
import { HomePillars } from './HomePillars'
import { HomePricingTeaser } from './HomePricingTeaser'
import { HomeFinalCta, HomeHowItWorks, HomeNetwork, HomeProblem } from './HomeSections'
import styles from './Page.module.css'

// The B2B home (site-relaunch Wave 1). Retargets from player-intent to the club
// owner: a 30-second hook + conversion. The former live sections (pulse /
// players / clubs) moved to the public /play hub — this home is fast + static
// except the proof strip. Copy is from
// docs/content/2026-07-18-site-relaunch-copy.md §1; the title/H1/lead/description
// were keyword-targeted per docs/seo/2026-07-26-seo-implementation-plan.md §Stage C.
const META = {
  // SEOHead appends ' — The Racket Center' → rendered <title> is 51 chars.
  title: 'Racket club management software',
  description:
    'The racket club management software for competitive clubs: tournaments, ratings, ladders, leagues and court bookings for tennis, padel and pickleball.',
}

const HERO = {
  eyebrow: 'For padel, tennis & pickleball clubs',
  h1: 'Racket club management software built for competition',
  subhead:
    'Run tournaments, TrueSkill ratings, ladders and leagues — plus court bookings, members and marketing — in one place. Racket club management software for padel, tennis and pickleball clubs.',
}

// The rolling ball and the scenes (docs/ux/2026-09-24-home-rolling-ball.md) load as
// their own chunk after the page has painted: the H1 carries the LCP, and nothing about
// the first paint may wait for decoration. They replace this page's scroll reveal;
// until they arrive, and if they never do, every block simply shows.
const HomeMotion = lazy(() => import('./HomeMotion').then((m) => ({ default: m.HomeMotion })))

export function HomePage() {
  const root = useRef<HTMLDivElement>(null)

  const jsonLd = [
    buildOrganizationJsonLd({
      description: META.description,
      legalName: 'Escuela de tenis ITS S.R.L.',
    }),
    buildSoftwareApplicationJsonLd({ path: '/', description: META.description, offers: PRICING_OFFERS }),
  ]

  return (
    <div className={styles.root} ref={root} data-track-root>
      <Suspense fallback={null}>
        <HomeMotion root={root} />
      </Suspense>
      <div className={styles.stage}>
        <div className={styles.page} data-track-content>
          <SEOHead title={META.title} description={META.description} canonicalPath="/" jsonLd={jsonLd} />

          {/* Hero text is intentionally NOT animated — the H1 carries the LCP. No
              backdrop behind it since the home split (2026-09-23): the page's scan
              lines, then two doors — one for a club, one for a player. */}
          <section className={styles.hero} aria-labelledby="home-hero-title" data-track-section>
            <div className={styles.heroContent}>
              <span className={styles.eyebrow}>{HERO.eyebrow}</span>
              <h1 id="home-hero-title" className={styles.h1}>
                {HERO.h1}
              </h1>
              <p className={styles.subhead}>{HERO.subhead}</p>
              <HomeDoors />
            </div>
          </section>

          <ProofStrip />
          <HomeProblem />
          <HomePillars />
          <HomeNetwork />
          <HomeHowItWorks />
          <HomePricingTeaser />
          <HomeFinalCta />
        </div>
      </div>
    </div>
  )
}
